import { ExtendedChip } from "@/components/common/standard-chip";
import TagListItem from "@/components/common/tag-list-item";
import { Popover, PopoverContent, PopoverTrigger } from "@/styles/ui/popover";
import { memo, useEffect, useRef, useState } from "react";
import { TagDetailPopoverContent } from "./tag-detail-popover";
import { MemberViewType } from "./types";

type MappingSearchResultItemProps = {
  label: string;
  tag: MemberViewType;
  onClick?: (tag: MemberViewType) => void;
  onClickBeforeAction?: () => void;
};

const MappingSearchResultItemComponent = ({
  label,
  tag,
  onClick,
  onClickBeforeAction,
}: MappingSearchResultItemProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
  const HOVER_DELAY_MS = 100; // hover 후 팝오버가 열리기까지의 딜레이 (ms)

  // hover 시 팝오버 열기/닫기
  const handleMouseEnter = () => {
    // 딜레이 후 팝오버 열기
    hoverTimerRef.current = setTimeout(() => {
      setIsPopoverOpen(true);
    }, HOVER_DELAY_MS);
  };

  const handleMouseLeave = () => {
    // 타이머 취소
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsPopoverOpen(false);
  };

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <TagListItem>
        {/* <TagListItem.Header>
            <TextChip className="bg-blue-50 text-blue-600">👍가장 유력해요!</TextChip>
            </TagListItem.Header> */}
        <PopoverTrigger asChild>
          <TagListItem.Body
            isSelected={tag.isSelected === "self"}
            onClick={() => {
              onClickBeforeAction?.();
              onClick?.(tag);
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {tag.isSum && <TagListItem.SigmaChip />}
            <TagListItem.Label>{label}</TagListItem.Label>
            <TagListItem.BadgeContainer>
              {!tag.isStandard && <ExtendedChip />}
            </TagListItem.BadgeContainer>
          </TagListItem.Body>
        </PopoverTrigger>
      </TagListItem>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={20}
        className="w-[320px] flex flex-col items-start rounded-xl shadow-2xl p-0"
        onMouseEnter={() => setIsPopoverOpen(true)}
        onMouseLeave={() => setIsPopoverOpen(false)}
      >
        <TagDetailPopoverContent
          prefix={tag.prefix}
          name={tag.name}
          label={label}
          type={tag.type}
          description={tag.description}
        />
      </PopoverContent>
    </Popover>
  );
};

const areTagsEqual = (prev: MemberViewType, next: MemberViewType) => {
  // children are intentionally omitted; this item only renders and clicks scalar tag metadata.
  return (
    prev.id === next.id &&
    prev.prefix === next.prefix &&
    prev.name === next.name &&
    prev.label === next.label &&
    prev.type === next.type &&
    prev.description === next.description &&
    prev.isSelected === next.isSelected &&
    prev.isSum === next.isSum &&
    prev.isStandard === next.isStandard &&
    prev.parent?.id === next.parent?.id &&
    prev.parent?.name === next.parent?.name &&
    prev.parent?.label === next.parent?.label &&
    prev.parent?.prefix === next.parent?.prefix &&
    prev.parent?.type === next.parent?.type &&
    prev.role?.key === next.role?.key
  );
};

export const MappingSearchResultItem = memo(
  MappingSearchResultItemComponent,
  // Keep onClick stable, for example via useTagClick, so unchanged items can skip rerendering.
  (prev, next) =>
    prev.label === next.label &&
    prev.onClick === next.onClick &&
    prev.onClickBeforeAction === next.onClickBeforeAction &&
    areTagsEqual(prev.tag, next.tag),
);
